import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiFilter, ApiSort } from "@result/rui/utils/api";

import ApiClient from "#/redux/api/ApiClient";
import { BLOOD_ANALYSIS_TYPE } from "#/redux/resource/codeLists/BloodAnalysisTypeCodeListSlice";
import { getErrorMessage } from "#/utils/APIUtils";

// Async thunk for fetching animal candidates for blood analysis
export const fetchAllOdkupZivalVoList = createAsyncThunk(
  "bloodAnalysis/fetchAllOdkupZivalVoList",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        "/api/dog/testna_postaja/odkup/zival/page",
        undefined,
        page,
        filter,
        sort
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getBloodAnalysisById = createAsyncThunk(
  "bloodAnalysis/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `/api/dog/testna_postaja/krvnaAnaliza/analiza/${id}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createBloodAnalysis = createAsyncThunk(
  "bloodAnalysis/create",
  async (dto, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(
        "/api/dog/testna_postaja/krvnaAnaliza/analiza",
        dto
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateBloodAnalysis = createAsyncThunk(
  "bloodAnalysis/update",
  async ({ id, dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(
        `/api/dog/testna_postaja/krvnaAnaliza/analiza/${id}`,
        dto
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteBloodAnalysis = createAsyncThunk(
  "bloodAnalysis/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.delete(
        `/api/dog/testna_postaja/krvnaAnaliza/analiza/${id}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getBloodAnalysisPage = createAsyncThunk(
  "bloodAnalysis/getPage",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        "/api/dog/testna_postaja/krvnaAnaliza/analiza/page",
        undefined,
        page,
        filter,
        sort
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getBloodAnalysisRecordsByOdkupZivalId = createAsyncThunk(
  "bloodAnalysis/getRecordsByOdkupZivalId",
  async (odkupZivalId, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        "/api/dog/testna_postaja/krvnaAnaliza/analiza/page",
        undefined,
        null,
        new ApiFilter().equal("odkupZivalId", odkupZivalId),
        "datumPopisa,desc"
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchBloodAnalysisCandidatesByGroup = createAsyncThunk(
  "bloodAnalysis/fetchCandidatesByGroup",
  async ({ page, groupId, tipKrvneAnalize }, { rejectWithValue }) => {
    try {
      const animalsResponse = await ApiClient.get(
        "/api/dog/testna_postaja/odkup/zival/page",
        undefined,
        page,
        new ApiFilter()
          .equal("odkup.skupina.id", groupId)
          .equal("zival.statusSifra", "AKTIVNA"),
        null
      );

      const { content: animals, size } = animalsResponse.data;

      if (!animals?.length) {
        return {
          content: [],
          number: 0,
          size: 0,
          totalElements: 0,
          totalPages: 0,
        };
      }

      const animalIds = animals.map((a) => a.id);

      const bloodAnalysisResponse = await ApiClient.get(
        "/api/dog/testna_postaja/krvnaAnaliza/analiza/page",
        undefined,
        { page: 0, size: 10000, sort: new ApiSort().desc("datumAnalize") },
        new ApiFilter().in("odkupZivalId", animalIds),
        null
      );

      const existingAnalyses = bloodAnalysisResponse.data.content ?? [];

      const analysesByAnimal = new Map();

      existingAnalyses.forEach((a) => {
        if (!analysesByAnimal.has(a.odkupZivalId)) {
          analysesByAnimal.set(a.odkupZivalId, []);
        }
        analysesByAnimal.get(a.odkupZivalId).push(a);
      });

      const toTime = (d) => (d ? new Date(d).getTime() : 0);

      const getLatestAnalysis = (list, type) => {
        return [...(list ?? [])]
          .filter((a) => a.tipKrvneAnalize === type)
          .sort((a, b) => {
            const diff = toTime(b.datumAnalize) - toTime(a.datumAnalize);
            if (diff !== 0) return diff;
            return toTime(b.createdOn) - toTime(a.createdOn);
          })[0];
      };

      const hasPositive = (analysis) =>
        analysis?.rezultati?.some((r) => r.rezultat === "+");

      const isCandidate = (animal) => {
        const animalAnalyses = analysesByAnimal.get(animal.id) ?? [];

        if (tipKrvneAnalize === BLOOD_ANALYSIS_TYPE.ZACETNI.id) {
          // candidate only if absolutely no analyses exist
          return animalAnalyses.length === 0;
        }

        if (tipKrvneAnalize === BLOOD_ANALYSIS_TYPE.KONCNI.id) {
          const hasInitial = animalAnalyses.some(
            (a) => a.tipKrvneAnalize === BLOOD_ANALYSIS_TYPE.ZACETNI.id
          );

          if (!hasInitial) return false;

          const latestFinal = getLatestAnalysis(
            animalAnalyses,
            BLOOD_ANALYSIS_TYPE.KONCNI.id
          );

          // no final yet → candidate
          if (!latestFinal) return true;

          // candidate only if last final is positive
          return hasPositive(latestFinal);
        }

        const latestOfType = getLatestAnalysis(animalAnalyses, tipKrvneAnalize);

        return !latestOfType;
      };

      const candidates = animals.filter(isCandidate);

      return {
        content: candidates,
        number: page,
        size,
        totalElements: candidates.length,
        totalPages: Math.ceil(candidates.length / size),
      };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialState = {
  list: null,
  current: null,
  page: null,
  pageOdkupZivalVo: {
    content: [],
    number: 0,
    size: 0,
    totalElements: 0,
    totalPages: 0,
  }, // Page<OdkupZivalVo>
  bloodAnalysisRecords: {
    content: [],
    number: 0,
    size: 0,
    totalElements: 0,
    totalPages: 0,
  }, // Page<KrvnaAnalizaVo>
  bloodAnalysisCandidates: {
    content: [],
    number: 0,
    size: 0,
    totalElements: 0,
    totalPages: 0,
  }, // Page<OdkupZivalVo>
  error: null,
  isLoading: false,
};

const bloodAnalysisSlice = createSlice({
  name: "bloodAnalysis",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrent: (state) => {
      state.current = null;
    },
    clearOdkupZivalVoList: (state) => {
      state.pageOdkupZivalVo = {
        content: [],
        number: 0,
        size: 0,
        totalElements: 0,
        totalPages: 0,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Get blood analysis by ID
      .addCase(getBloodAnalysisById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getBloodAnalysisById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.current = action.payload;
      })
      .addCase(getBloodAnalysisById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create blood analysis
      .addCase(createBloodAnalysis.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createBloodAnalysis.fulfilled, (state, action) => {
        state.isLoading = false;
        // POST endpoints return ApiResponseDto with output property
        state.current = action.payload.output;
      })
      .addCase(createBloodAnalysis.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update blood analysis
      .addCase(updateBloodAnalysis.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateBloodAnalysis.fulfilled, (state) => {
        state.isLoading = false;
        // PUT returns void, so we don't update state
      })
      .addCase(updateBloodAnalysis.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete blood analysis
      .addCase(deleteBloodAnalysis.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteBloodAnalysis.fulfilled, (state) => {
        state.isLoading = false;
        // DELETE returns ApiResponseDto, we don't update state
      })
      .addCase(deleteBloodAnalysis.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get paginated blood analyses
      .addCase(getBloodAnalysisPage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getBloodAnalysisPage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.page = action.payload;
      })
      .addCase(getBloodAnalysisPage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch animal candidates
      .addCase(fetchAllOdkupZivalVoList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllOdkupZivalVoList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pageOdkupZivalVo = action.payload;
      })
      .addCase(fetchAllOdkupZivalVoList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get blood analysis records by odkupZivalId
      .addCase(getBloodAnalysisRecordsByOdkupZivalId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        getBloodAnalysisRecordsByOdkupZivalId.fulfilled,
        (state, action) => {
          state.isLoading = false;
          state.bloodAnalysisRecords = action.payload;
        }
      )
      .addCase(
        getBloodAnalysisRecordsByOdkupZivalId.rejected,
        (state, action) => {
          state.isLoading = false;
          state.error = action.payload;
        }
      )
      // Fetch blood analysis candidates by group
      .addCase(fetchBloodAnalysisCandidatesByGroup.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchBloodAnalysisCandidatesByGroup.fulfilled,
        (state, action) => {
          state.isLoading = false;
          state.bloodAnalysisCandidates = action.payload;
        }
      )
      .addCase(
        fetchBloodAnalysisCandidatesByGroup.rejected,
        (state, action) => {
          state.isLoading = false;
          state.error = action.payload;
        }
      );
  },
});

export const { clearError, clearCurrent, clearOdkupZivalVoList } =
  bloodAnalysisSlice.actions;
export default bloodAnalysisSlice.reducer;
